import { Skeleton, Stack, Typography } from '@mui/material';
import { Avatar } from 'common/components/Avatar/Avatar';
import { AuthContext } from 'common/providers/AuthContextProvider/AuthContextProvider';
import { useContactUserRelationQuery, UserRelationType } from 'generated/graphql';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { styles } from './ContactWidget.styles';

interface ContactWidgetProps {
  onHasContact?: () => void;
}

export const ContactWidget: React.FC<ContactWidgetProps> = ({ onHasContact }) => {
  const { t } = useTranslation();
  const { isAuthenticated } = useContext(AuthContext);
  const { data, loading: loadingData } = useContactUserRelationQuery({ skip: !isAuthenticated });

  if (loadingData || !data?.userRelation) return null;

  onHasContact?.();

  const user = data?.userRelation?.subjectUser;

  const relationType = data?.userRelation?.relationType;

  const titles: Record<string, string | undefined> = {
    [UserRelationType.MainFinancialAdvisor]: t('contactWidget.title.mainFinancialAdvisor'),
    [UserRelationType.Scout]: t('contactWidget.title.scout'),
  };

  const title = !!relationType && relationType in titles ? titles[relationType] : '';
  const mailTo = `mailto:${user?.email}`;

  return (
    <Stack p={2} spacing={2} sx={styles.widget}>
      <Stack gap={2}>
        <Typography variant="headlineM">{title}</Typography>
        <Stack direction="row" gap={2}>
          {!!user?.avatar && (
            <Avatar
              alt={user?.displayName ?? user?.email}
              avatar={user?.avatar}
              size={66}
              sx={styles.avatar}
              variant="rounded"
            >
              <Skeleton />
            </Avatar>
          )}
          <Stack sx={styles.contact}>
            {user?.displayName && <Typography variant="bodyL">{user?.displayName}</Typography>}
            {user?.phoneNumber && (
              <Typography color="textSecondary" variant="bodyM">
                {user?.phoneNumber}
              </Typography>
            )}
            <Typography
              color="textSecondary"
              component="a"
              href={mailTo}
              onClick={(event) => {
                window.open(mailTo, 'mail');
                event.preventDefault();
              }}
              sx={styles.email}
              target="_blank"
              variant="bodyM"
            >
              {user?.email}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};
